import Link from 'next/link';
import { ReactElement, useRef, useState } from 'react';
import { Rating } from '@/entities/Activity/ui/Rating';
import { NewImagesGallery } from '@/entities/Activity/ui/ImagesGallery/NewImagesGallery';
import { Tomorrow } from '@/shared/images/icons/Tomorrow';
import { DropdownArrow } from '@/shared/images/icons/DropdownArrow';
import { TRatingStars } from '@/common/domain/Rating.domain';
import { numberFormatPrice } from '@/common/app/utils/numberUtils';

const DESCRIPTION_LIMIT = 100;

export type ActivityPreviewCardProps = {
    href: string;
    imageUrls?: string[];
    title: string;
    rating?: TRatingStars;
    ratingLabel?: string;
    reviews?: number;
    duration?: string;
    description?: string;
    price?: number;
    currency?: string;
    position?: number;
};

export const ActivityPreviewCard = ({
    href,
    imageUrls = [],
    title,
    rating,
    ratingLabel,
    reviews = 0,
    duration,
    description = '',
    price,
    currency,
}: ActivityPreviewCardProps): ReactElement => {
    const [readMore, setReadMore] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const longRead = description.length > DESCRIPTION_LIMIT;
    const priceLabel =
        price !== undefined && currency
            ? `${numberFormatPrice(price, {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: 0,
              })} ${currency.toUpperCase()}`
            : undefined;

    const toggleReadMore = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (readMore) scrollRef.current?.scrollTo?.(0, 0);
        setReadMore((active) => !active);
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <Link href={href} passHref prefetch={false}>
            <a
                className="relative flex h-[490px] w-full flex-col overflow-hidden border border-gray-4 bg-white shadow-trip-card transition hover:shadow-trip-card-hover dark:border-dark-2 dark:bg-dark-3 dark:shadow-trip-card-dark dark:hover:shadow-trip-card-hover-dark md:h-[550px] lg:h-[560px] rounded-2xl border-none"
                aria-label={title}
            >
                <NewImagesGallery
                    imageUrls={imageUrls.length ? imageUrls : ['/images/EmptyImg_mobile.png']}
                    withPaddings
                    event={false}
                    showLabels={false}
                    item_list_id=""
                    item_list_name=""
                    position={0}
                    additionalElement={
                        priceLabel ? (
                            <span className="tx-medium-medium absolute left-5 top-5 z-5 flex min-h-8 min-w-15 items-center justify-center rounded-full border border-gray-4 bg-white px-4 text-gray-1 md:min-h-12 md:min-w-24 md:px-5 md:tx-large-semibold">
                                {priceLabel}
                            </span>
                        ) : undefined
                    }
                />
                <div
                    className={`relative z-0 flex h-54 flex-col px-5 pt-5.5 md:h-52 md:px-6 md:pt-6 lg:h-58 ${
                        readMore ? '!h-auto' : ''
                    }`}
                >
                    <h2 className="tx-heading-4 my-1.5 line-clamp-2 shrink-0 overflow-hidden text-gray-1 dark:text-white">
                        {title}
                    </h2>
                    <div className="tx-large-regular mb-2.5 flex items-center text-gray-2 dark:text-gray-3 md:mb-3.5 lg:tx-medium-regular">
                        {reviews > 0 && rating && (
                            <>
                                <span className="mr-1.5">{ratingLabel}</span>
                                <Rating rating={rating} />
                                <span className="ml-1.5">({reviews.toLocaleString()})</span>
                            </>
                        )}
                        {!reviews && (
                            <span className="text-sand-dark-sand">Be the first to review!</span>
                        )}
                        {duration && (
                            <span className="ml-auto flex items-center">
                                <Tomorrow />
                                {duration}
                            </span>
                        )}
                    </div>
                    <div
                        ref={scrollRef}
                        className={`tx-medium-regular min-h-0 break-words text-gray-2 dark:text-white ${
                            readMore
                                ? 'max-h-52 w-[calc(100%+10px)] overflow-y-scroll pr-2'
                                : 'line-clamp-4 overflow-hidden'
                        }`}
                    >
                        {description}
                    </div>
                    {longRead && (
                        <button
                            type="button"
                            onClick={toggleReadMore}
                            className={`tx-medium-medium mt-auto flex min-h-9 shrink-0 w-full items-center pb-3.5 text-left text-green-normal bg-transparent border-none cursor-pointer rounded-xs ${
                                !readMore ? 'shadow-[0_-8px_16px_8px_var(--bg-white-dark3)]' : ''
                            }`}
                        >
                            {readMore ? 'See less' : 'Read more'}
                            <DropdownArrow className={`ml-1.5 ${readMore ? 'rotate-180' : ''}`} />
                        </button>
                    )}
                </div>
            </a>
        </Link>
    );
};

export default ActivityPreviewCard;
